import React, { useMemo, useState } from "react";
import submit from "submit-form";
import PropTypes from "prop-types";
import SvgIcon from "../../../../../parts/SvgIcon";
import { MealPlannerCalendar } from "./MealPlannerCalendar";
import {
  completeTemplateWeek,
  completeWeek,
  getDateWithDayToDisplay,
} from "../../../../../utils/date-helpers";
import { formatNutritionValue } from "../../../../../utils/nutrition-util";

export const MealPlannerWeekResume = ({ mealPlan, locals }) => {
  const { query, nutritionPlans, activeExercises } = locals;
  const { weeks = [] } = mealPlan;
  const {
    week = 1,
    plan = "",
    athlete,
    mealPlan: mealPlanId,
    viewMode,
  } = query;
  const selectedWeekContent = weeks[week - 1];
  const selectedWeek = selectedWeekContent?.days || [];
  const mealTypes = selectedWeek[0]?.meals
    .sort((a, b) => a.order - b.order)
    .map(({ type }) => type);

  const [openedItem, setOpenedItem] = useState(null); // Track the currently opened item
  const basicMealTypes = ["Breakfast", "Lunch", "Snack", "Dinner"];
  const selectedNutritionPlan = nutritionPlans
    ? nutritionPlans.find((n) => n._id === plan)
    : null;
  const isFromNutritionPlan = athlete && selectedNutritionPlan;

  const days = selectedNutritionPlan
    ? useMemo(
        () =>
          completeWeek(selectedWeek, activeExercises, selectedNutritionPlan),
        [selectedWeek, activeExercises, selectedNutritionPlan]
      )
    : completeTemplateWeek(selectedWeek);
  //get the meals for each date
  const mealsByDate = useMemo(() => {
    return selectedWeek.reduce((acc, { date, meals }, index) => {
      const key = date ?? `Day ${index + 1}`;
      acc[key] = meals;
      return acc;
    }, {});
  }, [selectedWeek]);

  /**
   * Function to open the modals
   * @param {*} modal
   * @param {*} date
   */
  const handleOpenModal = (modal, date) => {
    submit(`/actions/refresh/?`, {
      method: "GET",
      body: { "modal[type]": modal, ...(date && { date }) },
    });
  };

  /**
   * Function to delete a custom meal
   * @param {*} e
   */
  const handleDeleteCustomMeal = (e, meal) => {
    e.preventDefault();
    e.stopPropagation();
    if (
      process.browser &&
      confirm(`Are you sure you want to delete this meal and all its contents?`)
    ) {
      const body = {
        meal,
        bodyAction: "handle-custom-meal",
        handle: "delete",
      };

      submit(
        `/actions/meal-planner/update-days-meal-plan?mealPlanId=${mealPlanId}&proxyAthlete=${athlete}`,
        {
          method: "POST",
          body,
        }
      );
    }
  };

  /**
   * Function to remove all the foods of a day
   * @param {*} e
   * @param {*} date
   */
  const removeAllFoods = (e, date) => {
    e.preventDefault();
    e.stopPropagation();
    if (
      process.browser &&
      confirm(`Are you sure you want to delete all the content of this day?`)
    ) {
      const weekIndex = week - 1;
      const body = {
        weekIndex,
        date,
        bodyAction: "handle-food",
        handle: "delete",
        isTemplate: isFromNutritionPlan ? false : true,
      };

      submit(
        `/actions/meal-planner/update-days-meal-plan?mealPlanId=${mealPlanId}&proxyAthlete=${athlete}`,
        {
          method: "POST",
          body,
        }
      );
    }
  };

  return (
    <div className="resume">
      <div className="resume-nutrition">
        {days.map(({ date, goals, hasData, macrosTotal, hasFoods }, index) => {
          const isOpened = openedItem === index; // Check if the current item is opened
          return (
            <div key={`${date}-${index}`} className="resume-container">
              {/* header*/}
              <div className="resume-stats">
                <div
                  className={
                    hasData
                      ? `header-container bg-[--dark-grey]`
                      : `header-container bg-[--footer-background-color]`
                  }
                >
                  <div className="header-text">
                    <span>{getDateWithDayToDisplay(date)}</span>
                  </div>
                  {hasData && !viewMode && (
                    <div className="actions">
                      {/* <button className="copy">
                        <SvgIcon
                          src="/img/new-icons/vuesax-linear-document-copy.svg"
                          alt="copy"
                        />
                      </button> */}
                      <details
                        role="list"
                        open={isOpened && hasFoods}
                        className={hasFoods ? "enabled" : "disabled"}
                      >
                        <summary
                          onClick={(e) => {
                            e.preventDefault();
                            if (!hasFoods) {
                              return;
                            }
                            setOpenedItem(isOpened ? null : index);
                          }}
                          onBlur={(e) => {
                            e.preventDefault();
                            setOpenedItem(null);
                          }}
                          aria-haspopup="listbox"
                          className="!p-0 !h-auto"
                        >
                          <SvgIcon src="/img/new-icons/vuesax-linear-add-square.svg" />
                        </summary>
                        {hasFoods && (
                          <ul
                            role="listbox"
                            className={isOpened ? "opened" : ""}
                          >
                            <li>
                              <a
                                onMouseDown={() => {
                                  handleOpenModal("move-meal-plan", date);
                                }}
                              >
                                Move
                              </a>
                            </li>
                            <li>
                              <a
                                onMouseDown={() => {
                                  handleOpenModal("duplicate-meal-plan", date);
                                }}
                              >
                                Duplicate
                              </a>
                            </li>
                            <li>
                              <a
                                className="!text-[--danger]"
                                onMouseDown={(e) => removeAllFoods(e, date)}
                              >
                                Clear
                              </a>
                            </li>
                          </ul>
                        )}
                      </details>
                    </div>
                  )}
                </div>
                {/* content */}
                {isFromNutritionPlan && (
                  <div
                    className={
                      hasData
                        ? `content-container bg-[--dark-grey]`
                        : `content-container bg-[--footer-background-color]`
                    }
                  >
                    {["Proteins", "Carbs", "Fats"].map((label, index) => {
                      const macroKeys = ["proteins", "carbs", "fats", "fibers"];
                      const actual = formatNutritionValue(
                        macrosTotal[macroKeys[index]] || 0
                      );
                      const goal = formatNutritionValue(
                        goals[macroKeys[index]] || 0
                      );

                      const diff =
                        macrosTotal[macroKeys[index]] - goals[macroKeys[index]];
                      const absDiff = formatNutritionValue(Math.abs(diff));
                      const status = diff < 0 ? "left" : "excess";
                      return (
                        <div className="info" key={label}>
                          {hasData && (
                            <>
                              <div className="flex flex-col gap-[0.125rem]">
                                <span
                                  className={
                                    index === 0
                                      ? "protein"
                                      : index === 1
                                      ? "carbs"
                                      : index === 2
                                      ? "fat"
                                      : "fiber"
                                  }
                                >
                                  {label}
                                </span>
                                <span className="opacity-50">
                                  ({diff < 0 ? "Under" : "Over"})
                                </span>
                              </div>
                              <div className="goals">
                                <span>
                                  <span>
                                    {actual}/{goal}g
                                  </span>
                                </span>
                                <span className="opacity-50">
                                  ({absDiff} {status})
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {/* calendar */}
      <div
        className={
          isFromNutritionPlan
            ? "main-meal-planner-calendar"
            : "!mt-0 main-meal-planner-calendar"
        }
      >
        {mealTypes.map((meal, mealIndex) => (
          <div key={`${meal}-${mealIndex}`} className="meal-type-container">
            <div
              className={
                !basicMealTypes.includes(meal)
                  ? "meal-type-header justify-around"
                  : "meal-type-header justify-center"
              }
            >
              {!basicMealTypes.includes(meal) && !viewMode && (
                <div className="custom-meal-actions">
                  <a
                    onClick={(e) => {
                      handleDeleteCustomMeal(e, meal);
                    }}
                  >
                    <SvgIcon
                      src="/img/new-icons/red-trash.svg"
                      noFilterOnLight={true}
                    />
                  </a>
                  <a href="/actions/refresh?modal[type]=custom-meal">
                    <SvgIcon src="/img/new-icons/edit-pencil.svg" />
                  </a>
                </div>
              )}
              <span>{meal}</span>
            </div>
            <div className="meal-type-calendar">
              {days.map((day, dayIndex) => {
                const mealsForDay = mealsByDate[day.date] || [];
                const specificMeal =
                  mealsForDay.find((m) => m.type === meal) || {};
                return (
                  <MealPlannerCalendar
                    query={query}
                    meal={specificMeal}
                    day={day}
                    week={week}
                    viewMode={viewMode}
                    key={`${day?.date}-${dayIndex}`}
                  />
                );
              })}
            </div>
          </div>
        ))}
        {!viewMode && (
          <div className="add-custom-meal">
            <button
              onClick={() => {
                handleOpenModal("custom-meal");
              }}
            >
              <SvgIcon src="/img/new-icons/add-square-outline.svg" />
              <span>Custom Meal</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

MealPlannerWeekResume.propTypes = {
  mealPlan: PropTypes.object,
  locals: PropTypes.object,
};
